import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Star, Loader2, Shuffle, Search, Grid3X3, Type,
  Trophy, Check, X, HelpCircle, ChevronRight, RotateCcw, Sparkles, Clock, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECTS = [
  { key: 'general', label: 'General', color: 'from-emerald-500 to-teal-500', icon: '🔤' },
  { key: 'math', label: 'Math', color: 'from-red-500 to-orange-500', icon: '🔢' },
  { key: 'science', label: 'Science', color: 'from-violet-500 to-purple-500', icon: '🔬' },
  { key: 'reading', label: 'Reading', color: 'from-sky-500 to-blue-500', icon: '📖' },
  { key: 'bible', label: 'Bible', color: 'from-amber-500 to-yellow-500', icon: '✝' },
];

const PUZZLE_TYPES = [
  { key: 'scramble', label: 'Word Scramble', desc: 'Unscramble the letters!', icon: Shuffle, color: 'from-fuchsia-500 to-pink-500' },
  { key: 'search', label: 'Word Search', desc: 'Find hidden words!', icon: Search, color: 'from-cyan-500 to-blue-500' },
  { key: 'crossword', label: 'Crossword', desc: 'Solve clues to fill words!', icon: Grid3X3, color: 'from-amber-500 to-orange-500' },
  { key: 'missing', label: 'Missing Letters', desc: 'Fill in the blanks!', icon: Type, color: 'from-emerald-500 to-green-500' },
];

// ==================== WORD SCRAMBLE ====================
const WordScrambleGame = ({ data, onComplete }) => {
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (id, val) => setAnswers(p => ({ ...p, [id]: val }));

  const handleSubmit = () => {
    setSubmitted(true);
    const items = data.map(q => ({
      id: q.id, answer: q.answer, points: q.points,
      user_answer: (answers[q.id] || '').trim(),
    }));
    onComplete({ items });
  };

  return (
    <div className="space-y-4">
      {data.map((q, i) => {
        const isCorrect = submitted && (answers[q.id] || '').toLowerCase().trim() === q.answer;
        const isWrong = submitted && !isCorrect;
        return (
          <motion.div key={q.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.08 }}>
            <Card className={`border-2 transition-all ${submitted ? (isCorrect ? 'border-emerald-400 bg-emerald-50' : 'border-red-300 bg-red-50') : 'border-slate-200 hover:border-fuchsia-300'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-black tracking-[0.3em] text-fuchsia-600 bg-fuchsia-50 px-4 py-2 rounded-xl font-mono select-none">
                    {q.scrambled}
                  </div>
                  <div className="flex-1">
                    <Input
                      data-testid={`scramble-input-${i}`}
                      placeholder={`${q.length} letters...`}
                      value={answers[q.id] || ''}
                      onChange={e => handleChange(q.id, e.target.value)}
                      disabled={submitted}
                      maxLength={q.length + 2}
                      className={`text-lg font-semibold ${submitted ? (isCorrect ? 'border-emerald-400' : 'border-red-400') : ''}`}
                    />
                    <div className="flex items-center gap-2 mt-1">
                      {!revealed[q.id] ? (
                        <button onClick={() => setRevealed(p => ({ ...p, [q.id]: true }))}
                          className="text-xs text-slate-400 hover:text-fuchsia-500 flex items-center gap-1" data-testid={`hint-btn-${i}`}>
                          <HelpCircle className="w-3 h-3" /> Hint
                        </button>
                      ) : (
                        <span className="text-xs text-fuchsia-500 italic">{q.hint}</span>
                      )}
                      <span className="text-xs text-slate-400 ml-auto">{q.points} pts</span>
                    </div>
                  </div>
                  {submitted && (
                    <div className="flex-shrink-0">
                      {isCorrect ? <Check className="w-6 h-6 text-emerald-500" /> : (
                        <div className="text-center">
                          <X className="w-6 h-6 text-red-400 mx-auto" />
                          <p className="text-xs text-red-500 font-medium">{q.answer}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
      {!submitted && (
        <Button onClick={handleSubmit} className="w-full bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white h-12 text-lg font-bold"
          data-testid="scramble-submit-btn">
          <Check className="w-5 h-5 mr-2" /> Check Answers
        </Button>
      )}
    </div>
  );
};

// ==================== WORD SEARCH ====================
const WordSearchGame = ({ data, onComplete }) => {
  const [found, setFound] = useState([]);
  const [selecting, setSelecting] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const grid = data.grid;
  const words = data.words;

  const toggleCell = (r, c) => {
    if (submitted) return;
    const key = `${r}-${c}`;
    setSelecting(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key]);
  };

  const checkWord = () => {
    const selected = selecting.map(k => {
      const [r, c] = k.split('-').map(Number);
      return grid[r][c];
    }).join('');
    const reversed = selected.split('').reverse().join('');
    const match = words.find(w => w === selected || w === reversed);
    if (match && !found.includes(match)) {
      setFound(p => [...p, match]);
      toast.success(`Found "${match}"!`);
    }
    setSelecting([]);
  };

  const handleSubmit = () => {
    setSubmitted(true);
    onComplete({ found_words: found, all_words: words });
  };

  const allFound = found.length === words.length;

  return (
    <div className="space-y-4">
      <Card className="border-2 border-cyan-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {words.map(w => (
              <span key={w} className={`px-3 py-1 rounded-full text-sm font-bold transition-all ${found.includes(w)
                ? 'bg-emerald-100 text-emerald-700 line-through' : 'bg-cyan-50 text-cyan-700 border border-cyan-200'}`}
                data-testid={`search-word-${w}`}>
                {w}
              </span>
            ))}
          </div>
          <div className="inline-grid gap-0.5 bg-slate-100 p-2 rounded-xl mx-auto" style={{ gridTemplateColumns: `repeat(${data.size}, 1fr)` }}>
            {grid.map((row, r) => row.map((ch, c) => {
              const key = `${r}-${c}`;
              const isSelected = selecting.includes(key);
              const isFound = found.some(w => {
                // Simple highlight - just color found cells
                return false; // Skip complex highlight for simplicity
              });
              return (
                <button key={key}
                  onClick={() => toggleCell(r, c)}
                  data-testid={`grid-cell-${r}-${c}`}
                  className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-sm font-bold rounded transition-all select-none
                    ${isSelected ? 'bg-cyan-500 text-white scale-110 shadow-md' : 'bg-white text-slate-700 hover:bg-cyan-100'}`}>
                  {ch}
                </button>
              );
            }))}
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={checkWord} disabled={selecting.length < 2 || submitted} variant="outline"
              className="flex-1" data-testid="check-word-btn">
              <Search className="w-4 h-4 mr-1" /> Check Selection
            </Button>
            <Button onClick={() => setSelecting([])} variant="ghost" size="icon" data-testid="clear-selection-btn">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
      <p className="text-center text-sm text-slate-500">
        Found {found.length} of {words.length} words
      </p>
      {!submitted && (
        <Button onClick={handleSubmit} className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white h-12 text-lg font-bold"
          disabled={found.length === 0} data-testid="search-submit-btn">
          {allFound ? <><Trophy className="w-5 h-5 mr-2" /> Submit - All Found!</> : <><Check className="w-5 h-5 mr-2" /> Submit ({found.length}/{words.length})</>}
        </Button>
      )}
    </div>
  );
};

// ==================== CROSSWORD ====================
const CrosswordGame = ({ data, onComplete }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (id, val) => setAnswers(p => ({ ...p, [id]: val }));

  const handleSubmit = () => {
    setSubmitted(true);
    const items = data.map(q => ({
      id: q.id, answer: q.answer, points: q.points,
      user_answer: (answers[q.id] || '').trim(),
    }));
    onComplete({ items });
  };

  return (
    <div className="space-y-4">
      {data.map((q, i) => {
        const isCorrect = submitted && (answers[q.id] || '').toLowerCase().trim() === q.answer;
        const isWrong = submitted && !isCorrect;
        return (
          <motion.div key={q.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}>
            <Card className={`border-2 transition-all ${submitted ? (isCorrect ? 'border-emerald-400 bg-emerald-50' : 'border-red-300 bg-red-50') : 'border-slate-200 hover:border-amber-300'}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                    q.direction === 'across' ? 'bg-amber-100 text-amber-700' : 'bg-orange-100 text-orange-700'
                  }`}>
                    {q.number}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700 mb-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded mr-1 ${q.direction === 'across' ? 'bg-amber-100 text-amber-700' : 'bg-orange-100 text-orange-700'}`}>
                        {q.direction === 'across' ? 'ACROSS' : 'DOWN'}
                      </span>
                      {q.clue}
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid={`crossword-input-${i}`}
                        placeholder={`${q.length} letters`}
                        value={answers[q.id] || ''}
                        onChange={e => handleChange(q.id, e.target.value)}
                        disabled={submitted}
                        maxLength={q.length + 2}
                        className="font-mono text-lg tracking-widest uppercase"
                      />
                      <span className="text-xs text-slate-400 whitespace-nowrap">{q.points}pts</span>
                    </div>
                  </div>
                  {submitted && (
                    <div className="flex-shrink-0 mt-6">
                      {isCorrect ? <Check className="w-6 h-6 text-emerald-500" /> : (
                        <div className="text-center">
                          <X className="w-5 h-5 text-red-400 mx-auto" />
                          <p className="text-xs text-red-500">{q.answer}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
      {!submitted && (
        <Button onClick={handleSubmit} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white h-12 text-lg font-bold"
          data-testid="crossword-submit-btn">
          <Check className="w-5 h-5 mr-2" /> Check Answers
        </Button>
      )}
    </div>
  );
};

// ==================== MISSING LETTERS ====================
const MissingLettersGame = ({ data, onComplete }) => {
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (id, val) => setAnswers(p => ({ ...p, [id]: val }));

  const handleSubmit = () => {
    setSubmitted(true);
    const items = data.map(q => ({
      id: q.id, answer: q.answer, points: q.points,
      user_answer: (answers[q.id] || '').trim(),
    }));
    onComplete({ items });
  };

  return (
    <div className="space-y-4">
      {data.map((q, i) => {
        const isCorrect = submitted && (answers[q.id] || '').toLowerCase().trim() === q.answer;
        const isWrong = submitted && !isCorrect;
        return (
          <motion.div key={q.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.08 }}>
            <Card className={`border-2 transition-all ${submitted ? (isCorrect ? 'border-emerald-400 bg-emerald-50' : 'border-red-300 bg-red-50') : 'border-slate-200 hover:border-emerald-300'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-black tracking-[0.4em] text-emerald-600 bg-emerald-50 px-4 py-2 rounded-xl font-mono select-none">
                    {q.display}
                  </div>
                  <div className="flex-1">
                    <Input
                      data-testid={`missing-input-${i}`}
                      placeholder="Type the full word..."
                      value={answers[q.id] || ''}
                      onChange={e => handleChange(q.id, e.target.value)}
                      disabled={submitted}
                      maxLength={q.length + 2}
                      className={`text-lg font-semibold ${submitted ? (isCorrect ? 'border-emerald-400' : 'border-red-400') : ''}`}
                    />
                    <div className="flex items-center gap-2 mt-1">
                      {!revealed[q.id] ? (
                        <button onClick={() => setRevealed(p => ({ ...p, [q.id]: true }))}
                          className="text-xs text-slate-400 hover:text-emerald-500 flex items-center gap-1" data-testid={`missing-hint-${i}`}>
                          <HelpCircle className="w-3 h-3" /> Hint
                        </button>
                      ) : (
                        <span className="text-xs text-emerald-500 italic">{q.hint}</span>
                      )}
                      <span className="text-xs text-slate-400 ml-auto">{q.points} pts</span>
                    </div>
                  </div>
                  {submitted && (
                    <div className="flex-shrink-0">
                      {isCorrect ? <Check className="w-6 h-6 text-emerald-500" /> : (
                        <div className="text-center">
                          <X className="w-6 h-6 text-red-400 mx-auto" />
                          <p className="text-xs text-red-500 font-medium">{q.answer}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
      {!submitted && (
        <Button onClick={handleSubmit} className="w-full bg-gradient-to-r from-emerald-500 to-green-500 text-white h-12 text-lg font-bold"
          data-testid="missing-submit-btn">
          <Check className="w-5 h-5 mr-2" /> Check Answers
        </Button>
      )}
    </div>
  );
};

// ==================== RESULTS ====================
const PuzzleResults = ({ result, onPlayAgain, onChangePuzzle }) => (
  <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
    <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50">
      <CardContent className="p-8 text-center">
        <div className="mb-4">
          {result.score >= 90 ? (
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg">
              <Trophy className="w-10 h-10 text-white" />
            </div>
          ) : result.score >= 50 ? (
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
              <Star className="w-10 h-10 text-white" />
            </div>
          ) : (
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-sky-400 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
              <Zap className="w-10 h-10 text-white" />
            </div>
          )}
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-1">{result.message}</h2>
        <div className="flex items-center justify-center gap-6 my-4">
          <div className="text-center">
            <p className="text-4xl font-black text-amber-600">{result.score}%</p>
            <p className="text-xs text-slate-500">Score</p>
          </div>
          <div className="w-px h-10 bg-slate-200" />
          <div className="text-center">
            <p className="text-4xl font-black text-emerald-600">+{result.xp_earned}</p>
            <p className="text-xs text-slate-500">XP Earned</p>
          </div>
          <div className="w-px h-10 bg-slate-200" />
          <div className="text-center">
            <p className="text-4xl font-black text-sky-600">{result.points_earned}/{result.points_possible}</p>
            <p className="text-xs text-slate-500">Points</p>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button onClick={onPlayAgain} className="flex-1 bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white" data-testid="play-again-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Play Again
          </Button>
          <Button onClick={onChangePuzzle} variant="outline" className="flex-1" data-testid="change-puzzle-btn">
            <Shuffle className="w-4 h-4 mr-2" /> Different Puzzle
          </Button>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

// ==================== MAIN PAGE ====================
const WordPuzzles = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedType, setSelectedType] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('general');
  const [puzzle, setPuzzle] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [timer, setTimer] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    const fetchChild = async () => {
      try {
        const r = await api.get(`/children/${childId}`);
        setChild(r.data);
      } catch { navigate('/dashboard'); }
      finally { setLoading(false); }
    };
    fetchChild();
  }, [childId]);

  // Timer
  useEffect(() => {
    let interval;
    if (timerActive) {
      interval = setInterval(() => setTimer(t => t + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive]);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await api.get(`/word-puzzles/history/${childId}`);
      setHistory(r.data);
    } catch (e) { console.error(e); }
  };

  const generatePuzzle = async (type, subject) => {
    setGenerating(true);
    setResult(null);
    setTimer(0);
    try {
      const token = localStorage.getItem('token');
      const r = await api.get(`/word-puzzles/generate?puzzle_type=${type}&subject=${subject}&child_id=${childId}`);
      setPuzzle(r.data);
      setTimerActive(true);
    } catch (err) {
      toast.error('Failed to generate puzzle');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleComplete = async (answers) => {
    setTimerActive(false);
    try {
      const token = localStorage.getItem('token');
      const r = await api.post('/word-puzzles/submit', {
        child_id: childId,
        puzzle_type: selectedType,
        answers,
        time_spent: timer,
      });
      setResult(r.data);
      toast.success(`+${r.data.xp_earned} XP!`);
    } catch (err) {
      toast.error('Failed to submit puzzle');
      console.error(err);
    }
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    setPuzzle(null);
    setResult(null);
  };

  const handleStart = () => {
    generatePuzzle(selectedType, selectedSubject);
  };

  const handlePlayAgain = () => {
    setResult(null);
    generatePuzzle(selectedType, selectedSubject);
  };

  const handleChangePuzzle = () => {
    setPuzzle(null);
    setResult(null);
    setSelectedType(null);
    setTimerActive(false);
    setTimer(0);
  };

  const formatTime = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-purple-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-indigo-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="rounded-full"
                onClick={() => puzzle ? handleChangePuzzle() : navigate(`/learn/${childId}`)}
                data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-slate-800 text-lg">Word Puzzles</h1>
                <p className="text-xs text-slate-500">{child?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {timerActive && (
                <div className="flex items-center gap-1 bg-slate-100 px-3 py-1.5 rounded-full">
                  <Clock className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-sm font-mono font-bold text-slate-700" data-testid="timer">{formatTime(timer)}</span>
                </div>
              )}
              <div className="flex items-center gap-1 bg-indigo-100 rounded-full px-3 py-1.5">
                <Star className="w-4 h-4 text-indigo-500" />
                <span className="text-sm font-bold text-indigo-700">{child?.total_xp || 0} XP</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* Step 1: Choose puzzle type */}
          {!selectedType && (
            <motion.div key="select" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-800 mb-1">Choose a Puzzle</h2>
                <p className="text-slate-500">Pick a word puzzle to play!</p>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {PUZZLE_TYPES.map((pt) => (
                  <motion.div key={pt.key} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Card className="cursor-pointer border-2 border-slate-200 hover:border-indigo-300 transition-all h-full"
                      onClick={() => handleSelectType(pt.key)} data-testid={`puzzle-type-${pt.key}`}>
                      <CardContent className="p-5 text-center">
                        <div className={`w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br ${pt.color} flex items-center justify-center mb-3 shadow-md`}>
                          <pt.icon className="w-7 h-7 text-white" />
                        </div>
                        <h3 className="font-bold text-slate-800">{pt.label}</h3>
                        <p className="text-xs text-slate-500 mt-1">{pt.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* History summary */}
              <Button onClick={fetchHistory} variant="ghost" className="w-full text-slate-500" data-testid="view-history-btn">
                <Trophy className="w-4 h-4 mr-2" /> View My Puzzle History
              </Button>
              {history && (
                <Card className="mt-3 border-slate-200">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-indigo-600">{history.summary.total_completed}</p>
                        <p className="text-xs text-slate-500">Completed</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-emerald-600">{history.summary.average_score}%</p>
                        <p className="text-xs text-slate-500">Avg Score</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-600">{history.summary.total_xp}</p>
                        <p className="text-xs text-slate-500">Total XP</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {/* Step 2: Choose subject & start */}
          {selectedType && !puzzle && !generating && (
            <motion.div key="subject" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-slate-800 mb-1">
                  {PUZZLE_TYPES.find(t => t.key === selectedType)?.label}
                </h2>
                <p className="text-slate-500">Pick a subject for your words</p>
              </div>
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                {SUBJECTS.map((s) => (
                  <button key={s.key}
                    onClick={() => setSelectedSubject(s.key)}
                    data-testid={`subject-${s.key}`}
                    className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                      selectedSubject === s.key
                        ? `bg-gradient-to-r ${s.color} text-white shadow-md scale-105`
                        : 'bg-white text-slate-600 border border-slate-200 hover:border-indigo-300'
                    }`}>
                    <span className="mr-1">{s.icon}</span> {s.label}
                  </button>
                ))}
              </div>
              <Button onClick={handleStart} className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white h-14 text-lg font-bold shadow-lg"
                data-testid="start-puzzle-btn">
                <Sparkles className="w-5 h-5 mr-2" /> Start Puzzle
              </Button>
            </motion.div>
          )}

          {/* Loading */}
          {generating && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mb-3" />
              <p className="text-slate-500 font-medium">Generating your puzzle...</p>
            </motion.div>
          )}

          {/* Step 3: Play puzzle */}
          {puzzle && !result && !generating && (
            <motion.div key="play" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="text-center mb-4">
                <p className="text-sm text-slate-500">
                  <span className="capitalize font-medium">{puzzle.subject}</span>
                  {' · '}
                  <span className="capitalize">{puzzle.difficulty}</span>
                </p>
              </div>
              {selectedType === 'scramble' && <WordScrambleGame data={puzzle.data} onComplete={handleComplete} />}
              {selectedType === 'search' && <WordSearchGame data={puzzle.data} onComplete={handleComplete} />}
              {selectedType === 'crossword' && <CrosswordGame data={puzzle.data} onComplete={handleComplete} />}
              {selectedType === 'missing' && <MissingLettersGame data={puzzle.data} onComplete={handleComplete} />}
            </motion.div>
          )}

          {/* Step 4: Results */}
          {result && (
            <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <PuzzleResults result={result} onPlayAgain={handlePlayAgain} onChangePuzzle={handleChangePuzzle} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default WordPuzzles;
